import React, { useEffect, useRef, useState } from "react";
import styles from "./pastQueries.module.css";
import {
  categoryLabel,
  formatCpm,
  isWholeCpmInput,
  PublishedDetails,
  validateEditedCpm,
} from "./publishedDetails";

interface Props {
  details?: PublishedDetails;
  loading: boolean;
  loadFailed: boolean;
  onRetry: () => void;
  // Whether the viewer may change the price. Held back while a publish or
  // unpublish of the same query is in flight.
  canEdit: boolean;
  // Resolves when the new CPM is stored; rejects with the reason it was refused.
  onSaveCpm: (cpm: number) => Promise<void>;
}

// The CPM, category and category description of a published query, as rows of
// the Query Details popup. The CPM can be edited in place.
const PublishedCohortDetails = ({
  details,
  loading,
  loadFailed,
  onRetry,
  canEdit,
  onSaveCpm,
}: Props) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Focus goes to the field when editing starts and back to the Edit button
  // when it ends, so keyboard users never lose their place.
  const returnFocus = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (returnFocus.current) {
      returnFocus.current = false;
      editBtnRef.current?.focus();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(details?.cpm ? String(details.cpm) : "");
    setError("");
    setEditing(true);
  };

  const stopEdit = () => {
    returnFocus.current = true;
    setError("");
    setEditing(false);
  };

  const save = async () => {
    if (saving) return;
    const result = validateEditedCpm(draft);
    if (result.value === null) {
      setError(result.error);
      inputRef.current?.focus();
      return;
    }
    if (result.value === details?.cpm) {
      stopEdit();
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSaveCpm(result.value);
      stopEdit();
    } catch (e: any) {
      setError(e?.message || "Failed to update the CPM. Please try again.");
      inputRef.current?.focus();
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape" && !saving) {
      e.preventDefault();
      e.stopPropagation();
      stopEdit();
    }
  };

  if (!details && loading) {
    return (
      <div
        className={styles.queryDetailRow}
        role="status"
        aria-label="Loading CPM and category"
      >
        <span className={styles.queryDetailLabel}>CPM &amp; Category</span>
        <div className={styles.queryDetailValueWrap}>
          <span className={styles.queryDetailShimmer} />
        </div>
      </div>
    );
  }

  if (!details) {
    // Not in the federation's published list: the request failed, or the query
    // is no longer listed (its campaign was removed, say).
    return (
      <div className={styles.queryDetailRow}>
        <span className={styles.queryDetailLabel}>CPM &amp; Category</span>
        <div className={styles.queryDetailValueWrap}>
          <span
            className={styles.queryDetailMessage}
            role={loadFailed ? "alert" : undefined}
          >
            {loadFailed
              ? "Couldn't load the CPM and category."
              : "No CPM or category is listed for this query."}
          </span>
          {loadFailed && (
            <button
              type="button"
              className={styles.queryDetailLinkBtn}
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const description = details.categoryDescription.trim();

  return (
    <>
      <div className={styles.queryDetailRow}>
        {editing ? (
          <label
            className={styles.queryDetailLabel}
            htmlFor="published-cpm-input"
          >
            CPM (INR)
          </label>
        ) : (
          <span className={styles.queryDetailLabel}>CPM (INR)</span>
        )}
        {editing ? (
          <div className={styles.cpmEditField}>
            <div className={styles.cpmEditControls}>
              <input
                id="published-cpm-input"
                ref={inputRef}
                className={`${styles.cpmEditInput}${error ? ` ${styles.cpmEditInputInvalid}` : ""}`}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={9}
                value={draft}
                disabled={saving}
                aria-invalid={!!error}
                aria-describedby={error ? "published-cpm-error" : undefined}
                onChange={(e) => {
                  if (!isWholeCpmInput(e.target.value)) return;
                  setDraft(e.target.value);
                  if (error) setError("");
                }}
                onKeyDown={onKeyDown}
              />
              <button
                type="button"
                className={styles.cpmEditSaveBtn}
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className={styles.queryDetailLinkBtn}
                onClick={stopEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
            {error && (
              <span
                id="published-cpm-error"
                className={styles.cpmEditError}
                role="alert"
              >
                {error}
              </span>
            )}
          </div>
        ) : (
          <div className={styles.queryDetailValueWrap}>
            <span
              className={`${styles.queryDetailValue}${details.cpm ? "" : ` ${styles.queryDetailMuted}`}`}
            >
              {formatCpm(details.cpm)}
            </span>
            {canEdit && (
              <button
                type="button"
                ref={editBtnRef}
                className={styles.queryDetailLinkBtn}
                onClick={startEdit}
                aria-label={
                  details.cpm
                    ? `Edit CPM, currently ${formatCpm(details.cpm)}`
                    : "Set CPM"
                }
              >
                {details.cpm ? "Edit" : "Set"}
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.queryDetailRow}>
        <span className={styles.queryDetailLabel}>Category</span>
        <span className={styles.queryDetailValue}>
          {categoryLabel(details)}
        </span>
      </div>

      <div className={styles.queryDetailRow}>
        <span className={styles.queryDetailLabel}>Category Description</span>
        <span
          className={`${styles.queryDetailValue}${description ? "" : ` ${styles.queryDetailMuted}`}`}
        >
          {description || "—"}
        </span>
      </div>
    </>
  );
};

export default PublishedCohortDetails;
